import uniappClient from './uniappClient';

/**
 * Điểm thi tốt nghiệp: nhập bằng AI từ ảnh biên bản, tra cứu và nhập tay.
 *
 * Đi qua `uniappClient` chứ không phải `axiosClient` vì route nằm dưới `/v2/*`
 * của uniapp-backend, cùng chỗ với các endpoint DAT V2.
 *
 * Trước tính năng này, điểm tốt nghiệp không được lưu ở đâu cả: nhân viên xuất
 * bảng từ GetFly ra .xlsx, gõ tay rồi upload lại để in biên bản, file đọc xong
 * là vứt. Con số gốc chỉ nằm trên giấy.
 */
const API = '/v2/tot-nghiep';

/* ── Nhập bằng AI ────────────────────────────────────────────────────────── */

/**
 * Upload ảnh biên bản. Trả 202 — việc đọc chạy ở job nền, phải poll.
 *
 * @param {File[]} files ảnh (.jpg/.png/.webp) hoặc .pdf
 * @param {{khoaHoc?: string, note?: string, onUploadProgress?: Function}} opt
 *   `khoaHoc` TUỲ CHỌN: chọn khoá thì phạm vi khớp học viên hẹp lại rất nhiều
 *   (đo thật: 73 ca nhập nhằng xuống còn 5).
 */
async function createBatch(files, { khoaHoc = '', note = '', onUploadProgress } = {}) {
  const form = new FormData();
  [].concat(files || []).forEach((f) => form.append('files', f));
  if (khoaHoc) form.append('khoaHoc', khoaHoc);
  if (note) form.append('note', note);
  return uniappClient.post(`${API}/batch`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 10 * 60 * 1000,
    onUploadProgress,
  });
}

/** Danh sách lô đã upload, mới nhất trước. */
async function listBatches(params) {
  return uniappClient.get(`${API}/batch`, { params });
}

/** Một lô kèm toàn bộ dòng đã đọc. Dùng để poll khi status = 'extracting'. */
async function getBatch(id) {
  return uniappClient.get(`${API}/batch/${id}`);
}

/**
 * Sửa một dòng đã đọc.
 * @param {{corrections?: object, driving?: string|null, reviewed?: boolean}} body
 */
async function updateItem(id, body) {
  return uniappClient.patch(`${API}/item/${id}`, body);
}

/** Đọc lại cả lô (dùng khi có dòng lỗi). Tốn tiền API — hỏi lại trước khi gọi. */
async function retryBatch(id) {
  return uniappClient.post(`${API}/batch/${id}/retry`);
}

/** Ghi các dòng đã duyệt vào hệ thống. Bỏ `itemIds` để ghi tất cả. */
async function commitBatch(id, itemIds) {
  return uniappClient.post(`${API}/batch/${id}/commit`, itemIds ? { itemIds } : {});
}

async function deleteBatch(id) {
  return uniappClient.delete(`${API}/batch/${id}`);
}

/* ── Dashboard + nhập tay ────────────────────────────────────────────────── */

/** @param {{khoaHoc?, q?, thieuDiem?, nguon?, page?, limit?}} params */
async function listDiem(params) {
  return uniappClient.get(`${API}/diem`, { params });
}

/**
 * Sửa tay. Ô nào gửi lên thì ô đó chuyển nguồn sang 'tay' kèm tên người sửa.
 * @param {{diem?: object, ketLuan?: object, ketQua?: object, saHinh?: object, ngayKtThucHanh?: string, daoTao?: object, ghiChu?: string}} body
 *   `ketQua.{ktra5Mon,moPhong,ktraThucHanh}` là chuỗi tự do (KQKT 5 môn / KQ mô
 *   phỏng / KQ KT thực hành) — không suy ra từ điểm, gõ sao lưu vậy.
 *   `saHinh.{thoiGian,quangDuong}` là số, KHÔNG chặn trần 100 (quãng đường có
 *   thể tới ~210) — khác hẳn `diem.hinh` (điểm 0–100) và `daoTao.{tongGio,tongKm}`.
 */
async function updateDiem(id, body) {
  return uniappClient.patch(`${API}/diem/${id}`, body);
}

/** Tạo bản ghi trống cho một học viên chưa có (trường hợp không có giấy). */
async function createDiem(drivingId) {
  return uniappClient.post(`${API}/diem`, { driving: drivingId });
}

/* ── Chọn theo khoá (21/10/2026) ─────────────────────────────────────────── */

/**
 * Các khoá của trung tâm (lấy từ token), mới nhất trước, kèm số đếm
 * `{ soHocVien, soCoGetfly, soCoDiem }` cho dropdown. Chỉ đọc.
 */
async function listKhoa() {
  return uniappClient.get(`${API}/khoa`);
}

/**
 * Mọi học viên của một khoá (từ `drivings`), mỗi người một dòng, trộn với bản
 * ghi điểm nếu đã có: `_id` của bản ghi hoặc null, `drivingId`, `coGetfly`...
 * Chỉ đọc — KHÔNG tạo bản ghi; tạo bằng `createDiem` khi gõ ô đầu tiên.
 * @param {string} courseId
 * @param {{q?: string, thieuDiem?: boolean}} [params]
 */
async function listHocVienKhoa(courseId, params) {
  return uniappClient.get(`${API}/khoa/${courseId}/hoc-vien`, { params });
}

/* ── Đồng bộ GetFly ──────────────────────────────────────────────────────── */

/**
 * Chỉ đọc — không ghi gì lên GetFly. Trả về trường nào ghi được ngay (`ghi`),
 * trường nào GetFly đang có giá trị khác (`xungDot`, cần hỏi lại trước khi
 * ghi đè), trường nào bỏ qua vì ô đang trống (`boQuaRong`), và trường nào
 * chưa có mã GetFly để ghi (`chuaRoMaField` — hiện chỉ có hai ô SA HÌNH).
 */
async function getflySyncCheck(id) {
  return uniappClient.get(`${API}/diem/${id}/getfly-sync/check`);
}

/**
 * Ghi thật lên GetFly.
 * @param {string[]} [dongYGhiDe] danh sách `localKey` người dùng đã xác nhận
 *   ghi đè (lấy từ `xungDot` của lần `getflySyncCheck` gần nhất). Thiếu xác
 *   nhận cho một xung đột thì server trả 409 `UNRESOLVED_CONFLICTS`, không ghi
 *   phần nào cả.
 */
async function getflySync(id, dongYGhiDe) {
  return uniappClient.post(`${API}/diem/${id}/getfly-sync`, dongYGhiDe ? { dongYGhiDe } : {});
}

const totNghiepApi = {
  createBatch,
  listBatches,
  getBatch,
  updateItem,
  retryBatch,
  commitBatch,
  deleteBatch,
  listDiem,
  updateDiem,
  createDiem,
  listKhoa,
  listHocVienKhoa,
  getflySyncCheck,
  getflySync,
};

export default totNghiepApi;
